import type { TableNode } from '..';
import { isGroupRow } from './group-row-guards';

/**
 * Drops group rows (nodeType 'g'/'G', case-insensitive — see `isGroupRow`)
 * from a node list.
 *
 * Workflow row grouping (PSTUDIO-2220 / PSTUDIO-2349) is a UI-only feature:
 * group rows are stored in `tenancy.solution_version_nodes_ps` with
 * nodeType='g' but carry no compiler-relevant data — they only own their
 * member ids in `settings.subRows` plus a label in `settings.groupName`.
 *
 * Per Faisal Ahmad's comment on PSTUDIO-2220, group rows MUST never reach
 * the Bot Compiler or agent-compiler payloads — the compiler has strict
 * validation rules around group fields that we intentionally do not run.
 *
 * This helper is the single source of truth for that contract; call it at
 * every payload boundary. The columnOrder array used by the PIF serializer
 * already omits `settings`, so even if a G-row leaked through, its group
 * metadata wouldn't reach the compiler — but the row itself (with empty
 * required fields) would still trip validation. Hence: filter at every
 * boundary, defense-in-depth at the helper itself. The compare is
 * case-insensitive because `prepareRowsDataForPifCompiler` upper-cases
 * nodeType downstream and legacy data may already carry 'G' (PSTUDIO-3159).
 */
export const excludeGroupNodes = <T extends Pick<TableNode, 'nodeType'>>(
  rows: T[]
): T[] => rows.filter((row) => !isGroupRow(row));
